import { api } from './client';

export interface GrnItem {
  id: number;
  poItemId: number | null;
  itemId: number | null;
  itemName: string;
  poQty: string;
  receivedQty: string;
  shortQty: string;
  unit: string;
  unitPrice: string;
  total: string;
  quality: 'OK' | 'Damaged' | 'Rejected';
}

export type ScanStatus = 'Pending' | 'Verified' | 'Mismatch';

export interface GrnItemSerial {
  id: number;
  serialNo: string;
  scannedAt: string;
}

export interface GrnItemScanState {
  id: number;
  itemName: string;
  unit: string;
  receivedQty: number;
  scannedCount: number;
  isMatch: boolean;
  serials: GrnItemSerial[];
}

export interface GrnScanStatus {
  grnId: number;
  grnNo: string;
  scanStatus: ScanStatus;
  stockStatus: string;
  vendor: { id: number; name: string; mobile?: string; whatsapp?: string; email?: string };
  po: { id: number; poNo: string } | null;
  totalExpected: number;
  totalScanned: number;
  items: GrnItemScanState[];
}

export interface ScanCompleteResult {
  scanStatus: ScanStatus;
  allMatch: boolean;
  mismatches: Array<{ itemName: string; unit: string; expected: number; scanned: number; short: number }>;
}

export interface GrnScanReceipt {
  grnId: number;
  grnNo: string;
  grnDate: string;
  vendor: { id: number; name: string };
  po: { poNo: string } | null;
  scanStatus: ScanStatus;
  items: Array<{
    id: number;
    itemName: string;
    unit: string;
    receivedQty: number;
    serials: GrnItemSerial[];
  }>;
}

export interface Grn {
  id: number;
  grnNo: string;
  grnDate: string;
  /** Short subject, carried forward MRN → PR → PO → GRN. */
  subject?: string | null;
  poId: number;
  po?: { id: number; poNo: string };
  vendorId: number;
  vendor: { id: number; name: string };
  vendorInvoiceNo: string | null;
  vendorInvoiceDate: string | null;
  vehicleNo: string | null;
  qualityRemarks: string | null;
  stockStatus: 'Pending' | 'Received in Stock' | 'Quality Checked';
  scanStatus: ScanStatus;
  totalValue: string;
  poPendingQty?: number | null;
  items: GrnItem[];
}

export interface GrnFilter {
  status?: string;
  poId?: number;
  from?: string;
  to?: string;
  search?: string;
  page?: number;
  pageSize?: number;
}

export interface GrnListResult {
  data: Grn[];
  total: number;
  page: number;
  pageSize: number;
}

export async function listGrns(filter: GrnFilter = {}): Promise<GrnListResult> {
  const r = await api.get<GrnListResult>('/grn', { params: filter });
  return r.data;
}

/** Excel export of every record matching the filters + search (no page limit). */
export function grnExportPath(filter: GrnFilter): string {
  const { page: _p, pageSize: _s, ...rest } = filter;
  const qs = new URLSearchParams(
    Object.fromEntries(Object.entries(rest).filter(([, v]) => v != null && v !== '').map(([k, v]) => [k, String(v)])),
  ).toString();
  return `/grn/export.xlsx${qs ? `?${qs}` : ''}`;
}

export async function getGrn(id: number): Promise<Grn> {
  const r = await api.get<Grn>(`/grn/${id}`);
  return r.data;
}

export async function createGrn(payload: {
  grnDate: string;
  poId: number;
  vendorInvoiceNo?: string;
  vendorInvoiceDate?: string;
  vehicleNo?: string;
  qualityRemarks?: string;
  subject?: string;
  items: { poItemId: number; receivedQty: number; quality?: 'OK' | 'Damaged' | 'Rejected'; boxes?: number[] }[];
}): Promise<Grn> {
  const r = await api.post<Grn>('/grn', payload);
  return r.data;
}

export async function pendingStockEntry(): Promise<Grn[]> {
  const r = await api.get<Grn[]>('/grn/pending-stock-entry');
  return r.data;
}

// Open POs a GRN user can create a receipt against — served from the
// GRN module so receipt staff don't need the (broader) `po` permission.
export async function listAvailablePosForGrn(): Promise<{
  data: import('./po').Po[];
  total: number;
}> {
  const r = await api.get<{ data: import('./po').Po[]; total: number }>('/grn/available-pos');
  return r.data;
}

export async function confirmStockEntry(id: number): Promise<Grn> {
  const r = await api.post<Grn>(`/grn/${id}/confirm-stock-entry`);
  return r.data;
}

export async function deleteGrn(id: number): Promise<void> {
  await api.delete(`/grn/${id}`);
}

// ── Scan API ──────────────────────────────────────────────────────────────────

export async function getGrnScanStatus(id: number): Promise<GrnScanStatus> {
  const r = await api.get<GrnScanStatus>(`/grn/${id}/scan`);
  return r.data;
}

export async function addGrnSerial(
  grnId: number,
  grnItemId: number,
  serialNo: string,
): Promise<GrnItemSerial> {
  const r = await api.post<GrnItemSerial>(`/grn/${grnId}/scan/add`, { grnItemId, serialNo });
  return r.data;
}

export async function removeGrnSerial(grnId: number, serialId: number): Promise<void> {
  await api.delete(`/grn/${grnId}/scan/serial/${serialId}`);
}

export async function completeGrnScan(grnId: number): Promise<ScanCompleteResult> {
  const r = await api.post<ScanCompleteResult>(`/grn/${grnId}/scan/complete`);
  return r.data;
}

export async function getGrnScanReceipt(id: number): Promise<GrnScanReceipt> {
  const r = await api.get<GrnScanReceipt>(`/grn/${id}/scan/receipt`);
  return r.data;
}

// ── Labels API (auto-generated serials for printing) ──────────────────────────

export interface GrnLabelSerial {
  id: number;
  serialNo: string;
  itemId: number;
  status: string;
  /** Units this label represents — >1 for a box label. */
  qty?: number;
}

export interface GrnLabelsItem {
  grnItemId: number;
  item: { id: number; code: string; name: string; category: string };
  itemName: string;
  unit: string;
  receivedQty: number;
  serials: GrnLabelSerial[];
}

export interface GrnLabels {
  grnId: number;
  grnNo: string;
  grnDate: string;
  items: GrnLabelsItem[];
  totalSerials: number;
}

export async function getGrnLabels(id: number): Promise<GrnLabels> {
  const r = await api.get<GrnLabels>(`/grn/${id}/labels`);
  return r.data;
}

// ── Rack-based scan (Location + Serial) ───────────────────────────────────────

export interface RackScanStatus {
  grnId: number;
  grnNo: string;
  stockStatus: string;
  scanStatus: ScanStatus;
  totalPending: number;
  totalPlaced: number;
  totalSerials: number;
  items: Array<{
    grnItemId: number;
    itemId: number;
    itemCode: string;
    itemName: string;
    unit: string;
    total: number;
    placed: number;
    pending: number;
  }>;
  placements: Array<{
    serialId: number;
    serialNo: string;
    itemId: number;
    locationCode: string;
    locationName: string;
    scannedAt: string | null;
  }>;
}

export interface PlaceSerialResult {
  serial: { id: number; serialNo: string; itemId: number };
  location: { id: number; code: string; name: string; level: string };
  remainingPending: number;
  allPlaced: boolean;
}

export interface FinalizeScanResult {
  scanStatus: 'Verified';
  totalSerials: number;
}

export async function getGrnRackScanStatus(id: number): Promise<RackScanStatus> {
  const r = await api.get<RackScanStatus>(`/grn/${id}/scan/rack-status`);
  return r.data;
}

export async function placeGrnSerial(
  grnId: number,
  locationCode: string,
  serialNo: string,
): Promise<PlaceSerialResult> {
  const r = await api.post<PlaceSerialResult>(`/grn/${grnId}/scan/place`, {
    locationCode,
    serialNo,
  });
  return r.data;
}

export async function undoGrnPlacement(grnId: number, serialId: number): Promise<void> {
  await api.delete(`/grn/${grnId}/scan/place/${serialId}`);
}

export async function finalizeGrnRackScan(grnId: number): Promise<FinalizeScanResult> {
  const r = await api.post<FinalizeScanResult>(`/grn/${grnId}/scan/finalize`);
  return r.data;
}
